import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigCatProvider } from "configcat-react";
import { ReactNode, useEffect } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";

import { Toast } from "@climatepartner/components";
import { Spinner } from "@climatepartner/components";
import { AuthProvider, useAuth } from "@climatepartner/hooks";

import { DatePickerProvider } from "./components/DatePicker";
import ErrorBoundary from "./components/ErrorBoundary";
import { Layout } from "./components/Layout";
import { MaintenanceModeProvider } from "./components/Maintenance/Maintenance";
import { ThemeProvider } from "./components/ThemeProvider";
import httpClient from "./helpers/http.client";
import Homepage from "./pages/Homepage";
import { AbstractLogger, LogLevel } from "./plugins/logger/AbstractLogger";
import HomescreenLogger from "./plugins/logger/HomescreenLogger";
import { LoggerProvider } from "./plugins/logger/LoggerProvider";
import { AbstractMonitor } from "./plugins/monitor/AbstractMonitor";
import HomescreenMonitor from "./plugins/monitor/HomescreenMonitor";
import { MonitorProvider } from "./plugins/monitor/MonitorProvider";
import { I18nextProvider } from "./plugins/translations";
import { IS_NODE_ENV_PRODUCTION } from "./variables";

interface Tracking {
    monitor: AbstractMonitor;
    logger: AbstractLogger;
    logLevel: LogLevel;
}

const initTracking = (): Tracking => {
    const analyticsCookiesEnabled = document.cookie.includes("analytics:yes");
    const monitor = HomescreenMonitor(analyticsCookiesEnabled);
    const logger = HomescreenLogger(analyticsCookiesEnabled);
    const logLevel: LogLevel = IS_NODE_ENV_PRODUCTION ? "WARN" : "DEBUG";

    document.addEventListener("cookieyes_consent_update", (eventData: any) => {
        const { accepted } = eventData.detail;
        if (accepted.includes("analytics")) {
            window.location.reload();
        }
    });

    return { monitor, logger, logLevel };
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            staleTime: 30000,
        },
    },
});

const router = createBrowserRouter([
    {
        Component: () => (
            <Providers>
                <Layout />
            </Providers>
        ),
        children: [
            {
                path: "/",
                element: <Homepage />,
            },
            {
                path: "*",
                element: <Navigate to="/" />,
            },
        ],
    },
]);

const HttpTokenProvider = ({ children }: { children: ReactNode }) => {
    const { user, isAuthenticated } = useAuth();

    useEffect(() => {
        if (isAuthenticated && user?.accessToken) {
            httpClient.defaults.headers.common = { Authorization: `Bearer ${user.accessToken}` };
        }
    }, [isAuthenticated, user?.accessToken]);

    return <>{children}</>;
};

const FeatureToggleProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth();
    const configcatApiKey = window.$cp.config_cat_sdk_key;

    if (!configcatApiKey || !user) {
        console.error("ConfigCat API key or user is missing");
        return <Spinner isFixed />;
    }

    return (
        <ConfigCatProvider
            sdkKey={configcatApiKey}
            options={{
                defaultUser: {
                    email: user.email,
                    identifier: user.id,
                    custom: { email: user.email },
                },
            }}
        >
            <MaintenanceModeProvider>{children}</MaintenanceModeProvider>
        </ConfigCatProvider>
    );
};

const Providers = ({ children }: { children: ReactNode }) => {
    const { monitor, logger, logLevel } = initTracking();

    return (
        <I18nextProvider>
            <ThemeProvider>
                <DatePickerProvider>
                    <AuthProvider environment={window.$cp.environment}>
                        <HttpTokenProvider>
                            <FeatureToggleProvider>
                                <QueryClientProvider client={queryClient}>
                                    <MonitorProvider monitor={monitor}>
                                        <LoggerProvider logger={logger} logLevel={logLevel}>
                                            <ErrorBoundary>{children}</ErrorBoundary>
                                        </LoggerProvider>
                                    </MonitorProvider>
                                </QueryClientProvider>
                            </FeatureToggleProvider>
                        </HttpTokenProvider>
                    </AuthProvider>
                </DatePickerProvider>
                <Toast />
            </ThemeProvider>
        </I18nextProvider>
    );
};

const App = () => <RouterProvider router={router} />;

export default App;
